import { Box, Checkbox, useMediaQuery, useTheme } from '@mui/material';
import DeleteModal from 'components/delete-modal';
import { UPTanStackTable, UPTanStackTableColumn } from 'components/tanstack-table';
import { useTableState } from 'components/tanstack-table/hooks/useTableState';
import { CompanyRole } from 'constants/CompanyRole';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import {
  deleteDocumentAction,
  downloadDocumentAction,
  downloadMultipleDocumentAction,
  getDocumentsAction,
} from 'store/actions/documents-action';
import { documentsPaginationSelector, documentsSelector } from 'store/selectors/documents-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  CONTRACTS__ARE_YOU_SURE,
  CONTRACTS__SELECTED,
  DATAGRID__NO_ROWS,
  DOCUMENTS__A1_DECLARATION,
  DOCUMENTS__ABSENCE_CERTIFICATE,
  DOCUMENTS__BANKING_MANDATE,
  DOCUMENTS__BANK_CARD,
  DOCUMENTS__BURDEN_OF_PROOF_EMPLOYEE,
  DOCUMENTS__BURDEN_OF_PROOF_EMPLOYER,
  DOCUMENTS__C4,
  DOCUMENTS__CERTIFICATE_OF_GOOD_CONDUCT,
  DOCUMENTS__CODE_95,
  DOCUMENTS__CONFIRMATION_SHEET,
  DOCUMENTS__CONTRACT,
  DOCUMENTS__CV,
  DOCUMENTS__DEVIATION_REGISTER,
  DOCUMENTS__DOCUMENT_CATEGORY,
  DOCUMENTS__DOCUMENT_NAME,
  DOCUMENTS__DRIVER_CARD,
  DOCUMENTS__DRIVING_LICENSE,
  DOCUMENTS__EMPLOYEE,
  DOCUMENTS__EMPLOYMENT_CERTIFICATE,
  DOCUMENTS__FISCAL_FILE,
  DOCUMENTS__ID,
  DOCUMENTS__INDIVIDUAL_ACCOUNT,
  DOCUMENTS__INTENTION_DECLARATION,
  DOCUMENTS__INVOICE,
  DOCUMENTS__MEDICAL_CHECK,
  DOCUMENTS__PARTNERSHIP_AGREEMENT,
  DOCUMENTS__PAYSLIP,
  DOCUMENTS__PERIOD_END,
  DOCUMENTS__PERIOD_START,
  DOCUMENTS__RESIDENCE_CARD,
  DOCUMENTS__SAFETY_PASSPORT,
  DOCUMENTS__SHARED_FILES,
  DOCUMENTS__SOCIAL_DOCUMENT,
  DOCUMENTS__STUDENT_PROOF_OF_INSCRIPTION,
  DOCUMENTS__VACATION_CERTIFICATE,
  DOCUMENTS__VCA,
  DOCUMENTS__WORKPOSTFILE,
  DOCUMENTS__WORK_PERMIT,
  GENERAL__CLEAR_ALL,
  GENERAL__DOWNLOAD_SELECTED,
  GENERAL__SELECT_ALL,
} from 'translations/constants';
import { IDocument } from 'types/documents';
import { IViewDocument } from 'types/documents/IDocument';
import { treatAsUTC } from 'utils/helpers';
import { ReactComponent as DeleteIcon } from '../../assets/icons/DeleteGrey.svg';
import { ReactComponent as DocumentIcon } from '../../assets/icons/DocumentGridIcon.svg';
import { ReactComponent as DownloadIcon } from '../../assets/icons/DownloadGrey.svg';
import DocumentMobileCard from './DocumentMobileCard';
import './styles.scss';
import { DocumentCategories, DocumentGridViewProps } from './types';

// Memoized cell components to avoid react/no-unstable-nested-components warning
const DocumentIconCell = memo(() => (
  <Box display="flex" justifyContent="center" alignItems="center">
    <DocumentIcon style={{ width: '24px', height: '24px' }} />
  </Box>
));

interface ActionsCell {
  rowId: number;
  hoveredRowId: number | undefined;
  userCanDownload: (id: number) => boolean;
  userCanDelete: (id: number) => boolean;
  onDownload: (id: number) => void;
  onDelete: (id: number) => void;
}

const ActionsCell = memo(
  ({ rowId, hoveredRowId, userCanDownload, userCanDelete, onDownload, onDelete }: ActionsCell) => {
    if (hoveredRowId === rowId) {
      return (
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '8px',
            height: '24px',
          }}
        >
          {userCanDownload(rowId) && (
            <DownloadIcon
              className="documentsIcon"
              data-testid={`documents-download-${rowId}`}
              onClick={() => onDownload(rowId)}
            />
          )}
          {userCanDelete(rowId) && (
            <DeleteIcon
              className="documentsIcon"
              data-testid={`documents-delete-${rowId}`}
              onClick={() => onDelete(rowId)}
            />
          )}
        </Box>
      );
    }
    return <Box style={{ height: '24px' }} />;
  },
);

const DocumentsGridView = (props: DocumentGridViewProps): JSX.Element => {
  const { companyId, category, employeeId, startDate, endDate, searchQuery } = props;

  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(768));

  const documents = useAppSelector(documentsSelector);
  const paginationMeta = useAppSelector(documentsPaginationSelector);

  const [idToDelete, setIdToDelete] = useState<number>();
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [showManagerWarning, setShowManagerWarning] = useState<boolean>(false);

  const isEmployee = useAppSelector(profileSelector).role === CompanyRole.EMPLOYEE;
  const [clearAll, setClearAll] = useState<boolean>(true);

  // Selection state with "select all + exclusions" support
  const [selectAllMode, setSelectAllMode] = useState<boolean>(false);
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());

  // Map column IDs to backend field names
  const mapColumnToBackendField = useCallback((columnId: string): string => {
    const mapping: Record<string, string> = {
      name: 'name',
      periodStart: 'periodStart',
      periodEnd: 'periodEnd',
      categoryName: 'category',
      employeeName: 'employeeName',
    };
    return mapping[columnId] || columnId;
  }, []);

  // Table state with server-side pagination & sorting
  const tableState = useTableState({
    initialPageSize: 25,
    initialPageIndex: 0, // TanStack uses 0-based
  });

  // Fetch documents whenever pagination, sorting, or filters change
  const fetchDocuments = useCallback(() => {
    // Don't fetch if date range is incomplete (one date set without the other)
    if ((!startDate && endDate) || (startDate && !endDate)) {
      return;
    }

    dispatch(
      getDocumentsAction({
        companyId,
        category,
        employeeId,
        startDate: startDate ? treatAsUTC(startDate) : startDate,
        endDate: endDate ? treatAsUTC(endDate) : endDate,
        searchQuery,
        page: tableState.pagination.pageIndex + 1, // Convert 0-based to 1-based for backend
        pageSize: tableState.pagination.pageSize,
        sortBy: tableState.sorting[0] ? mapColumnToBackendField(tableState.sorting[0].id) : undefined,
        sortOrder: tableState.sorting[0]?.desc ? 'desc' : 'asc',
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    companyId,
    category,
    employeeId,
    startDate,
    endDate,
    searchQuery,
    tableState.pagination.pageIndex,
    tableState.pagination.pageSize,
    tableState.sorting,
    mapColumnToBackendField,
  ]);

  // Trigger fetch when pagination or sorting changes
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const userRole = useAppSelector(profileSelector).role;
  const isAdmin = userRole === CompanyRole.ADMIN;
  const isManager =
    userRole === CompanyRole.COMPANY_MANAGER ||
    userRole === CompanyRole.LOCATION_MANAGER ||
    userRole === CompanyRole.HR_MANAGER;

  const documentCategoriesArray = useMemo(
    () => [
      t(DOCUMENTS__CONTRACT),
      t(DOCUMENTS__INVOICE),
      t(DOCUMENTS__ID),
      t(DOCUMENTS__WORKPOSTFILE),
      t(DOCUMENTS__BANKING_MANDATE),
      t(DOCUMENTS__STUDENT_PROOF_OF_INSCRIPTION),
      t(DOCUMENTS__VCA),
      t(DOCUMENTS__MEDICAL_CHECK),
      t(DOCUMENTS__SAFETY_PASSPORT),
      t(DOCUMENTS__BANK_CARD),
      t(DOCUMENTS__DRIVING_LICENSE),
      t(DOCUMENTS__WORK_PERMIT),
      t(DOCUMENTS__CONFIRMATION_SHEET),
      t(DOCUMENTS__PARTNERSHIP_AGREEMENT),
      t(DOCUMENTS__SHARED_FILES),
      t(DOCUMENTS__DEVIATION_REGISTER),
      t(DOCUMENTS__SOCIAL_DOCUMENT),
      t(DOCUMENTS__INDIVIDUAL_ACCOUNT),
      t(DOCUMENTS__VACATION_CERTIFICATE),
      t(DOCUMENTS__EMPLOYMENT_CERTIFICATE),
      t(DOCUMENTS__PAYSLIP),
      t(DOCUMENTS__FISCAL_FILE),
      t(DOCUMENTS__INTENTION_DECLARATION),
      t(DOCUMENTS__ABSENCE_CERTIFICATE),
      t(DOCUMENTS__DRIVER_CARD),
      t(DOCUMENTS__C4),
      t(DOCUMENTS__CV),
      t(DOCUMENTS__CODE_95),
      t(DOCUMENTS__RESIDENCE_CARD),
      t(DOCUMENTS__BURDEN_OF_PROOF_EMPLOYEE),
      t(DOCUMENTS__BURDEN_OF_PROOF_EMPLOYER),
      t(DOCUMENTS__CERTIFICATE_OF_GOOD_CONDUCT),
      t(DOCUMENTS__A1_DECLARATION),
    ],
    [t],
  );

  const padTo2Digits = num => {
    return num.toString().padStart(2, '0');
  };

  const formatDateToString = useCallback((date: Date): string => {
    if (!date) return '';
    const auxDate = new Date(date);
    return [auxDate.getFullYear(), padTo2Digits(auxDate.getMonth() + 1), padTo2Digits(auxDate.getDate())].join('-');
  }, []);

  const generateDocumentsWithCategories = useCallback(
    (docs: IDocument[]): IViewDocument[] => {
      const auxDocs = docs.map(d => {
        return {
          ...d,
          employeeName: d.employeeName ?? '',
          name: d.name.length > 39 ? `${d.name.substring(0, 39)}...` : d.name,
          categoryName: documentCategoriesArray[d.category - 1],
          periodStart: formatDateToString(d.periodStart),
          periodEnd: formatDateToString(d.periodEnd),
        };
      });
      return auxDocs;
    },
    [documentCategoriesArray, formatDateToString],
  );

  // Memoize processed documents to prevent table resets on hover/selection changes
  const processedDocuments = useMemo(() => {
    return generateDocumentsWithCategories(documents);
  }, [documents, generateDocumentsWithCategories]);

  const findDocumentName = useCallback(
    (id: number) => {
      const found = documents.find(doc => doc.id === id);
      return found.name;
    },
    [documents],
  );

  // Categories that managers cannot delete or download
  const managerForbiddenCategories = useMemo(
    () => [
      Number(DocumentCategories.ID),
      Number(DocumentCategories.MEDICAL_CHECK),
      Number(DocumentCategories.SAFETY_PASSPORT),
      Number(DocumentCategories.DRIVING_LICENCE),
      Number(DocumentCategories.WORK_PERMIT),
    ],
    [],
  );

  const deleteForbiddenCategories = useMemo(() => [Number(DocumentCategories.A1_DECLARATION)], []);

  const userCanDeleteCheck = useCallback(
    (docId: number) => {
      if (isEmployee) return false;
      if (isAdmin) return true;

      const documentType = documents.find(doc => doc.id === docId).category;
      if (deleteForbiddenCategories.includes(documentType)) return false;
      if (managerForbiddenCategories.includes(documentType)) return false;
      return true;
    },
    [documents, isEmployee, isAdmin, managerForbiddenCategories, deleteForbiddenCategories],
  );

  const userCanDownloadCheck = useCallback(
    (docId: number) => {
      if (!isManager) {
        return true;
      }

      const documentType = documents.find(doc => doc.id === docId).category;
      if (managerForbiddenCategories.includes(documentType)) {
        return false;
      }

      return true;
    },
    [documents, isManager, managerForbiddenCategories],
  );

  const onDeleteDocument = useCallback(
    (id: number) => {
      dispatch(
        deleteDocumentAction({
          companyId: documents[0].companyId,
          id,
          category,
          startDate,
          endDate,
          employeeId,
          searchQuery,
          page: tableState.pagination.pageIndex + 1,
          pageSize: tableState.pagination.pageSize,
          sortBy: tableState.sorting[0] ? mapColumnToBackendField(tableState.sorting[0].id) : undefined,
          sortOrder: tableState.sorting[0]?.desc ? 'desc' : 'asc',
        }),
      );
    },
    [
      category,
      dispatch,
      documents,
      endDate,
      startDate,
      employeeId,
      searchQuery,
      tableState.pagination,
      tableState.sorting,
      mapColumnToBackendField,
    ],
  );

  const onDownloadDocument = useCallback(
    (id: number) => {
      dispatch(
        downloadDocumentAction({
          companyId: documents[0].companyId,
          id,
          name: findDocumentName(id),
        }),
      );
    },
    [dispatch, documents, findDocumentName],
  );

  const onDeleteButtonPress = useCallback((id: number) => {
    setIdToDelete(id);
    setOpenDeleteModal(true);
  }, []);

  // Memoized cell renderers
  const iconCellRenderer = useCallback(() => <DocumentIconCell />, []);

  const actionsCellRenderer = useCallback(
    ({ row }) => (
      <ActionsCell
        rowId={row.id}
        hoveredRowId={tableState.hoveredRowId as number}
        userCanDownload={userCanDownloadCheck}
        userCanDelete={userCanDeleteCheck}
        onDownload={onDownloadDocument}
        onDelete={onDeleteButtonPress}
      />
    ),
    [tableState.hoveredRowId, userCanDownloadCheck, userCanDeleteCheck, onDownloadDocument, onDeleteButtonPress],
  );

  const tanStackColumns: UPTanStackTableColumn<IViewDocument>[] = useMemo(
    () => [
      {
        id: 'icon',
        header: '',
        size: 50,
        align: 'center',
        headerAlign: 'center',
        enableSorting: false,
        cell: iconCellRenderer,
      },
      {
        id: 'name',
        header: t(DOCUMENTS__DOCUMENT_NAME),
        accessorKey: 'name',
        size: 320,
        align: 'left',
        headerAlign: 'left',
      },
      {
        id: 'periodStart',
        header: t(DOCUMENTS__PERIOD_START),
        accessorKey: 'periodStart',
        size: 130,
        align: 'left',
        headerAlign: 'left',
      },
      {
        id: 'periodEnd',
        header: t(DOCUMENTS__PERIOD_END),
        accessorKey: 'periodEnd',
        size: 130,
        align: 'left',
        headerAlign: 'left',
      },
      {
        id: 'categoryName',
        header: t(DOCUMENTS__DOCUMENT_CATEGORY),
        accessorKey: 'categoryName',
        size: 130,
        align: 'left',
        headerAlign: 'left',
      },
      {
        id: 'employeeName',
        header: t(DOCUMENTS__EMPLOYEE),
        accessorKey: 'employeeName',
        size: 200,
        align: 'left',
        headerAlign: 'left',
      },
      {
        id: 'actions',
        header: '',
        size: 100,
        align: 'right',
        headerAlign: 'right',
        enableSorting: false,
        cell: actionsCellRenderer,
      },
    ],
    [t, iconCellRenderer, actionsCellRenderer],
  );

  const areSelectedRowsIds = useMemo(() => {
    return (
      tableState.selectedRows.length > 0 &&
      tableState.selectedRows.some(id =>
        documents.some(doc => doc.id === id && doc.category === Number(DocumentCategories.ID)),
      )
    );
  }, [tableState.selectedRows, documents]);

  const canDownloadMultiple = !isManager || (isManager && !areSelectedRowsIds);

  const onDownloadDocuments = useCallback(() => {
    if (selectAllMode) {
      // Download all with exclusions
      dispatch(
        downloadMultipleDocumentAction({
          companyId,
          selectAll: true,
          excludedIds: Array.from(excludedIds),
          filters: {
            category,
            employeeId,
            startDate: startDate ? treatAsUTC(startDate) : startDate,
            endDate: endDate ? treatAsUTC(endDate) : endDate,
            searchQuery,
          },
          zipName: 'documents.zip',
        }),
      );
    } else {
      // Download specific IDs
      dispatch(
        downloadMultipleDocumentAction({
          companyId,
          ids: tableState.selectedRows as number[],
          zipName: 'documents.zip',
        }),
      );
    }
    if (!canDownloadMultiple) {
      setShowManagerWarning(true);
      setTimeout(() => setShowManagerWarning(false), 10000);
    }
  }, [
    dispatch,
    companyId,
    selectAllMode,
    excludedIds,
    tableState.selectedRows,
    category,
    employeeId,
    startDate,
    endDate,
    searchQuery,
    canDownloadMultiple,
  ]);

  // Footer actions configuration for the generic table footer
  const footerActions = useMemo(
    () => [
      {
        id: 'download',
        label: t(GENERAL__DOWNLOAD_SELECTED),
        icon: <DownloadIcon />,
        onClick: onDownloadDocuments,
        ariaLabel: 'Download selected documents',
        title: 'Download selected documents',
      },
    ],
    [onDownloadDocuments, t],
  );

  const warningRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showManagerWarning && warningRef.current) {
      warningRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showManagerWarning]);

  // Custom mobile card renderer
  const renderMobileCard = useCallback(
    (row: IViewDocument, actions: React.ReactNode, checkbox: React.ReactNode, isSelected: boolean) => (
      <DocumentMobileCard
        row={row}
        actions={actions}
        checkbox={checkbox}
        isSelected={isSelected}
        userCanDownload={userCanDownloadCheck}
        userCanDelete={userCanDeleteCheck}
        onDownload={onDownloadDocument}
        onDelete={onDeleteButtonPress}
      />
    ),
    [userCanDownloadCheck, userCanDeleteCheck, onDownloadDocument, onDeleteButtonPress],
  );

  // Select all functionality
  const allDocumentIds = useMemo(() => processedDocuments.map(doc => doc.id), [processedDocuments]);

  // Determine checkbox state based on selection mode
  const isAllOnPageSelected = useMemo(() => {
    if (selectAllMode) {
      // In select-all mode, header is checked if NO exclusions exist globally
      return excludedIds.size === 0;
    }
    // In normal mode, check if all items on current page are selected
    return tableState.selectedRows.length === allDocumentIds.length && allDocumentIds.length > 0;
  }, [selectAllMode, excludedIds.size, allDocumentIds, tableState.selectedRows.length]);

  const isSomeSelected = useMemo(() => {
    if (selectAllMode) {
      // In select-all mode, indeterminate if SOME (but not all) items are excluded globally
      const hasExclusions = excludedIds.size > 0;
      const allExcluded = excludedIds.size >= paginationMeta.totalItems;
      return hasExclusions && !allExcluded;
    }
    // In normal mode
    return tableState.selectedRows.length > 0 && tableState.selectedRows.length < allDocumentIds.length;
  }, [selectAllMode, excludedIds.size, paginationMeta.totalItems, allDocumentIds, tableState.selectedRows.length]);

  const handleSelectAllOnPage = useCallback(() => {
    if (selectAllMode) {
      // Already in select-all mode
      // Check if there are ANY exclusions globally, not just on current page
      const hasExclusions = excludedIds.size > 0;

      if (hasExclusions) {
        // There are some exclusions - this means NOT everything is selected
        // So clicking header checkbox should SELECT ALL (clear all exclusions)
        setExcludedIds(new Set());
      } else {
        // No exclusions - everything is selected
        // So clicking should DESELECT ALL and exit select-all mode
        setSelectAllMode(false);
        setExcludedIds(new Set());
        tableState.clearSelection();
        setClearAll(true);
      }
    } else if (isAllOnPageSelected) {
      // Not in select-all mode yet
      // Deselecting all
      tableState.clearSelection();
      setClearAll(true);
    } else {
      // Selecting all - enter select-all mode by default
      setSelectAllMode(true);
      setExcludedIds(new Set());
      tableState.clearSelection();
      setClearAll(false);
    }
  }, [isAllOnPageSelected, tableState, selectAllMode, excludedIds]);

  // Clear all selections (called from footer's "Clear all" button)
  const handleClearAll = useCallback(() => {
    setSelectAllMode(false);
    setExcludedIds(new Set());
    tableState.clearSelection();
    setClearAll(true);
  }, [tableState]);

  // Get selection count for display
  const getSelectionCount = useCallback(() => {
    if (selectAllMode) {
      return paginationMeta.totalItems - excludedIds.size;
    }
    return tableState.selectedRows.length;
  }, [selectAllMode, paginationMeta.totalItems, excludedIds.size, tableState.selectedRows.length]);

  // Compute which rows on current page should show as selected
  const currentPageSelectedRows = useMemo(() => {
    if (clearAll) return [];

    if (selectAllMode) {
      // In select-all mode, show all current page items as selected except excluded ones
      return allDocumentIds.filter(id => !excludedIds.has(id));
    }

    // In normal mode, show explicitly selected rows
    return tableState.selectedRows;
  }, [selectAllMode, allDocumentIds, excludedIds, tableState.selectedRows, clearAll]);

  // Handle selection change from table (individual row checkboxes and clear all)
  const handleSelectionChange = useCallback(
    (selectedIds: (string | number)[]) => {
      // If empty array, it's a "clear all" action from footer
      if (selectedIds.length === 0) {
        handleClearAll();
        return;
      }

      if (selectAllMode) {
        // In select-all mode, track exclusions
        const newExclusions = new Set(excludedIds);

        // Update exclusions based on current page selections
        allDocumentIds.forEach(id => {
          if (!selectedIds.includes(id)) {
            newExclusions.add(id);
          } else {
            newExclusions.delete(id);
          }
        });

        // If all items are now excluded, exit select-all mode
        if (newExclusions.size >= paginationMeta.totalItems) {
          setSelectAllMode(false);
          setExcludedIds(new Set());
          tableState.clearSelection();
          setClearAll(true);
        } else {
          setExcludedIds(newExclusions);
        }
      } else {
        // Normal mode - this shouldn't happen with our new logic, but keep for safety
        tableState.setSelectedRows(selectedIds);
        setClearAll(selectedIds.length === 0);
      }
    },
    [selectAllMode, excludedIds, allDocumentIds, tableState, paginationMeta.totalItems, handleClearAll],
  );

  return (
    <Box mt={2}>
      {isMobile && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Checkbox
            checked={isAllOnPageSelected}
            indeterminate={isSomeSelected}
            onChange={handleSelectAllOnPage}
            size="medium"
          />
          <Box sx={{ fontSize: '14px', fontWeight: 500, color: '#212121' }}>{t(GENERAL__SELECT_ALL)}</Box>
        </Box>
      )}
      <UPTanStackTable
        data={processedDocuments}
        columns={tanStackColumns}
        setRowOnHover={tableState.setHoveredRowId}
        getRowId={row => row.id}
        // Server-side pagination
        manualPagination
        pageCount={paginationMeta.totalPages}
        totalItems={paginationMeta.totalItems}
        pagination={tableState.pagination}
        onPaginationChange={tableState.setPagination}
        // Server-side sorting
        manualSorting
        sorting={tableState.sorting}
        onSortingChange={tableState.setSorting}
        // Selection
        withCheckboxes
        selectedRows={currentPageSelectedRows}
        disableSelectionOnClick
        onSelectionChange={handleSelectionChange}
        customHeaderCheckbox={{
          checked: isAllOnPageSelected,
          indeterminate: isSomeSelected,
          onChange: handleSelectAllOnPage,
        }}
        footerActions={footerActions}
        footerClearAllText={t(GENERAL__CLEAR_ALL)}
        footerSelectedText={t(CONTRACTS__SELECTED)}
        footerCanClearAll
        footerSelectedCount={getSelectionCount()}
        footerTotalCount={paginationMeta.totalItems}
        emptyMessage={t(DATAGRID__NO_ROWS)}
        mobileBreakpoint={768}
        renderMobileCard={renderMobileCard}
        sx={{ height: '100%' }}
      />
      {openDeleteModal && (
        <DeleteModal
          content={t(CONTRACTS__ARE_YOU_SURE)}
          open={openDeleteModal}
          testIdSuffix={idToDelete}
          onClose={() => setOpenDeleteModal(false)}
          onDelete={() => {
            onDeleteDocument(idToDelete);
          }}
        />
      )}
    </Box>
  );
};

export default DocumentsGridView;
